import React, { useState } from 'react';
import { View, Pressable } from 'react-native';

import Text from '~/client/components/Text';
import Image from '~/client/components/Image';
import MusicProgressBar from '~/client/components/MusicProgressBar';
import { useMusicKit, RepeatMode } from '~/client/utils/MusicKitContext';
import { useLoggedIn } from '~/client/utils/LoggedInContext';
import isWeb from '~/client/utils/isWeb';

const ACCENT_COLOR = '#FA4058';

function formatTime(totalSeconds: number) {
  if (!totalSeconds || Number.isNaN(totalSeconds)) {
    return '0:00';
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function repeatModeGlyph(repeatMode: number) {
  if (repeatMode === RepeatMode.one) {
    return '🔂';
  }
  return '🔁';
}

// A route-independent "now playing" bar, mounted once at the app root so it
// stays visible across navigation instead of disappearing with whichever
// song/embed screen started playback.
export default function GlobalMusicPlayerBar() {
  const musicKit = useMusicKit();
  const loggedInUser = useLoggedIn();
  const [showQueue, setShowQueue] = useState(false);

  if (!isWeb() || !musicKit.nowPlayingCatalogId) {
    return null;
  }

  const isLoading = musicKit.pendingCatalogId === musicKit.nowPlayingCatalogId;
  const progress =
    musicKit.currentPlaybackDuration > 0
      ? musicKit.currentPlaybackTime / musicKit.currentPlaybackDuration
      : 0;
  const upNext = musicKit.queueItems.slice(musicKit.queuePosition + 1);

  return (
    <View
      // @ts-expect-error `fixed` is a web-only position value supported by react-native-web, but not in RN's ViewStyle types
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 50,
      }}
    >
      {showQueue && upNext.length > 0 ? (
        <View
          className="bg-white dark:bg-gray-800 border-t border-neutral-200 dark:border-gray-900"
          style={{ maxHeight: 200, overflow: 'scroll', padding: 12 }}
        >
          {upNext.map((item, index) => (
            <View
              key={`${item.id}-${index}`}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <View style={{ flex: 1 }}>
                <Text style={{ fontWeight: 'bold', fontSize: 13 }}>
                  {item.title}
                </Text>
                <Text style={{ fontSize: 12 }}>{item.artistName}</Text>
              </View>
              <Pressable
                onPress={() =>
                  musicKit.removeFromQueue(musicKit.queuePosition + 1 + index)
                }
                // @ts-expect-error `cursor` is a web-only style property
                style={{ marginLeft: 12, cursor: 'pointer' }}
              >
                <Text style={{ fontSize: 14 }}>✕</Text>
              </Pressable>
            </View>
          ))}
        </View>
      ) : null}

      <View
        className="flex flex-row items-center px-4 py-2 bg-white dark:bg-gray-800 border-t border-neutral-200 dark:border-gray-900"
      >
        {musicKit.nowPlayingArtworkURL ? (
          <Image
            src={musicKit.nowPlayingArtworkURL
              .replace('{w}', '40')
              .replace('{h}', '40')}
            style={{ width: 40, height: 40, borderRadius: 4, marginRight: 12 }}
            resizeMode="contain"
          />
        ) : null}

        <Pressable
          onPress={() => musicKit.skipToPrevious()}
          // @ts-expect-error `cursor` is a web-only style property
          style={{ marginRight: 10, cursor: 'pointer' }}
        >
          <Text style={{ color: ACCENT_COLOR, fontSize: 18 }}>⏮</Text>
        </Pressable>

        <Pressable
          disabled={isLoading}
          onPress={() => musicKit.togglePlayPause()}
          style={{
            marginRight: 10,
            opacity: isLoading ? 0.4 : 1,
            // @ts-expect-error `cursor` is a web-only style property
            cursor: isLoading ? 'default' : 'pointer',
          }}
        >
          <Text style={{ color: ACCENT_COLOR, fontSize: 22 }}>
            {isLoading ? '⋯' : musicKit.isPlaying ? '⏸' : '▶'}
          </Text>
        </Pressable>

        <Pressable
          onPress={() => musicKit.skipToNext()}
          // @ts-expect-error `cursor` is a web-only style property
          style={{ marginRight: 12, cursor: 'pointer' }}
        >
          <Text style={{ color: ACCENT_COLOR, fontSize: 18 }}>⏭</Text>
        </Pressable>

        <View style={{ flex: 1 }}>
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Text style={{ fontWeight: 'bold' }}>
              {musicKit.nowPlayingTitle}
            </Text>
            <Text style={{ fontSize: 12 }}>
              {formatTime(musicKit.currentPlaybackTime)} /{' '}
              {formatTime(musicKit.currentPlaybackDuration)}
            </Text>
          </View>
          <Text>{musicKit.nowPlayingArtistName}</Text>
          <MusicProgressBar
            progress={progress}
            color={ACCENT_COLOR}
            onSeek={(fraction) =>
              musicKit.seekToTime(fraction * musicKit.currentPlaybackDuration)
            }
          />
        </View>

        {musicKit.nowPlayingSongId ? (
          <Pressable
            onPress={() => {
              if (!loggedInUser?.email) {
                window.alert('Please log in to use Favorites.');
                return;
              }
              musicKit.toggleFavorite();
            }}
            // @ts-expect-error `cursor` is a web-only style property
            style={{ marginLeft: 12, cursor: 'pointer' }}
          >
            <Text style={{ color: ACCENT_COLOR, fontSize: 18 }}>
              {musicKit.isNowPlayingFavorite ? '★' : '☆'}
            </Text>
          </Pressable>
        ) : null}

        <Pressable
          onPress={() => musicKit.cycleRepeatMode()}
          // @ts-expect-error `cursor` is a web-only style property
          style={{ marginLeft: 12, cursor: 'pointer' }}
        >
          <Text
            style={{
              fontSize: 18,
              opacity: musicKit.repeatMode === RepeatMode.none ? 0.35 : 1,
            }}
          >
            {repeatModeGlyph(musicKit.repeatMode)}
          </Text>
        </Pressable>

        {upNext.length > 0 ? (
          <Pressable
            onPress={() => setShowQueue((current) => !current)}
            // @ts-expect-error `cursor` is a web-only style property
            style={{ marginLeft: 12, cursor: 'pointer' }}
          >
            <Text style={{ fontSize: 12 }}>Up Next ({upNext.length})</Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}
